/**
 * The sheet: the one right-hand surface a record opens in.
 *
 * Any surface opens it with React content (`openSheet`), widens it so the
 * record takes the room and the rail folds to a strip (`setWide`), and closes
 * it. The shell reads the same store to stamp `.app.open` and `.app.wide`, so
 * there is one answer to "is a sheet open" for every part of the page.
 *
 * Escape folds in order: a wide sheet narrows first, then the sheet closes
 * (`escapeSheet`). ⌘K belongs to Search and ⌘J to the Ask dock.
 */
import { useSyncExternalStore, type ReactNode } from "react";

export interface SheetState {
  /** The sheet's content; null when no sheet is open. */
  content: ReactNode | null;
  wide: boolean;
}

const CLOSED: SheetState = { content: null, wide: false };

export interface SheetStore {
  getSnapshot: () => SheetState;
  subscribe: (listener: () => void) => () => void;
  open: (content: ReactNode, options?: { wide?: boolean }) => void;
  close: () => void;
  setWide: (wide: boolean) => void;
  toggleWide: () => void;
}

export function createSheetStore(): SheetStore {
  let state: SheetState = CLOSED;
  const listeners = new Set<() => void>();
  const set = (next: SheetState) => {
    if (next.content === state.content && next.wide === state.wide) return;
    state = next;
    for (const listener of listeners) listener();
  };
  return {
    getSnapshot: () => state,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    open: (content, options) => set({ content, wide: options?.wide ?? false }),
    close: () => set(CLOSED),
    // A closed sheet has nothing to widen.
    setWide: (wide) => set({ content: state.content, wide: state.content == null ? false : wide }),
    toggleWide: () =>
      set({ content: state.content, wide: state.content == null ? false : !state.wide }),
  };
}

/** The app's one sheet. */
export const sheetStore: SheetStore = createSheetStore();

/** Test-only: close the app sheet between tests. */
export function __resetSheetForTests(): void {
  sheetStore.close();
}

/**
 * Escape's rule for the sheet: a wide sheet narrows, an open one closes.
 * Returns whether it did anything, so the caller knows Escape was used.
 */
export function escapeSheet(store: SheetStore = sheetStore): boolean {
  const { content, wide } = store.getSnapshot();
  if (content == null) return false;
  if (wide) store.setWide(false);
  else store.close();
  return true;
}

export interface UseSheet extends SheetState {
  isOpen: boolean;
  open: SheetStore["open"];
  close: SheetStore["close"];
  setWide: SheetStore["setWide"];
  toggleWide: SheetStore["toggleWide"];
}

export function useSheet(store: SheetStore = sheetStore): UseSheet {
  const state = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
  return {
    ...state,
    isOpen: state.content != null,
    open: store.open,
    close: store.close,
    setWide: store.setWide,
    toggleWide: store.toggleWide,
  };
}
